import {yupResolver} from "@hookform/resolvers/yup";
import {Button, Stack, TextField} from "@mui/material";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {useNavigate} from "react-router-dom";
import useApiRequest from "../../hooks/useApiRequest";
import {snackbar} from "../../utils/snackbar";

const getSchema = (isNew) =>
  yup.object().shape({
    name: yup
      .string()
      .required("El nombre es obligatorio")
      .max(255, "Máximo 255 caracteres")
      .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]*$/, "Solo se permiten letras y espacios"),

    surname: yup
      .string()
      .required("El apellido es obligatorio")
      .max(255, "Máximo 255 caracteres")
      .matches(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]*$/, "Solo se permiten letras y espacios"),

    email: yup
      .string()
      .required("El email es obligatorio")
      .email("Debe ser un email válido")
      .max(255, "Máximo 255 caracteres"),

    ts_email: yup.string().nullable().notRequired().email("Debe ser un email válido").max(255, "Máximo 255 caracteres"),

    password: isNew
      ? yup
          .string()
          .notRequired()
          .nullable()
          .test(
            "password-strength",
            "La contraseña debe contener al menos una minúscula, una mayúscula, un número y un carácter especial (@$!%*?&)",
            (value) => !value || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,255}$/.test(value)
          )
      : yup.string().nullable().optional(),

    confirmPassword: isNew
      ? yup
          .string()
          .notRequired()
          .nullable()
          .when("password", {
            is: (password) => password && password.length > 0,
            then: (schema) =>
              schema
                .required("Debe repetir la contraseña")
                .oneOf([yup.ref("password")], "Las contraseñas no coinciden"),
            otherwise: (schema) => schema.notRequired()
          })
      : yup.string().nullable().optional()
  });

const UserForm = ({
  isNew,
  refreshData,
  setIsEditing,
  initialUserData,
  canManageTsEmail = true,
  canEditAllFields = true
}) => {
  const navigate = useNavigate();
  const {update, create} = useApiRequest({baseUrl: `/users`, resource: "users"});

  const handleSave = async (data) => {
    let filteredData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== "" && value !== null && value !== undefined)
    );

    if (!isNew) {
      delete filteredData.password;
      delete filteredData.confirmPassword;
    }

    const result = isNew ? await create(filteredData) : await update(initialUserData.id, filteredData);
    if (!result.success) return;

    refreshData();
    setIsEditing(false);

    if (isNew && result.data?.id) {
      navigate("/user-form", {state: {id: result.data?.id}});
      snackbar.success("Usuario creado correctamente");
    } else {
      snackbar.success("Usuario actualizado correctamente");
    }
  };

  const {
    register,
    handleSubmit,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(getSchema(isNew)),
    defaultValues: initialUserData
  });

  const onSubmit = (data) => {
    handleSave(data);
  };

  const onInvalid = (validationErrors) => {
    const firstMessage = Object.values(validationErrors)[0]?.message;
    snackbar.error(firstMessage || "Revisa los campos del formulario.");
  };

  return (
    <Stack direction="column" spacing={2} minWidth={"300px"}>
      <TextField
        label="Nombre"
        {...register("name")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.name}
        helperText={errors.name?.message}
        variant="outlined"
        disabled={!canEditAllFields}
      />
      <TextField
        label="Apellido"
        {...register("surname")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.surname}
        helperText={errors.surname?.message}
        variant="outlined"
        disabled={!canEditAllFields}
      />
      <TextField
        label="Email"
        {...register("email")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.email}
        helperText={errors.email?.message}
        variant="outlined"
        disabled={!canEditAllFields}
      />
      <TextField
        label="TS Email"
        {...register("ts_email")}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.ts_email}
        helperText={errors.ts_email?.message}
        variant="outlined"
        disabled={!canManageTsEmail}
      />
      {isNew && (
        <>
          <TextField
            label="Contraseña"
            type="password"
            {...register("password")}
            slotProps={{htmlInput: {maxLength: 255}}}
            error={!!errors.password}
            helperText={errors.password?.message}
            variant="outlined"
          />
          <TextField
            label="Repetir contraseña"
            type="password"
            {...register("confirmPassword")}
            slotProps={{htmlInput: {maxLength: 255}}}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            variant="outlined"
          />
        </>
      )}
      <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit, onInvalid)}>
        {isNew ? "Crear" : "Guardar"}
      </Button>
    </Stack>
  );
};

export default UserForm;
